// Player KPI page (print, full roster): Player KPI Page.dc.html from the design project, with the reflow rule its
// "Player KPI Reflow" variant tested: played cards 3 across in possessions order; row spacing and type step down
// (airy -> roomy -> normal -> compact -> tight) until the page fits; did-not-play wraps 6 across. One letter page.
// A one-sheet page has a last step, micro, for six rows of cards: tight holds five (15 played), and a preseason game
// can play 17-18 (2026-10-07: the packet clipped its bottom row and footer at 17).
import {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  buildPlayers,
  gameMeta,
  KpiModel,
  liveMeta,
  packetPageLabel,
  periodLabel,
  PlayerModel,
  satStatus,
} from "./kpiLib";
import {
  Footer,
  Legend,
  Masthead,
  usePrinting,
  ValueBlock,
  WindowHead,
} from "./common";
import type { KpiResponse } from "./types";

const DISP = "var(--pr-disp)";

const DENSITY = {
  airy: {
    gap: "14px 10px",
    kt: 10,
    kb: 11,
    hs: "0.4in",
    bfs: "19px",
    gfs: "17px",
    nfs: "14.5px",
    bpad: "3px 7px 3px 6px",
    hpad: "6px 8px 6px 5px",
    tfs: "23px",
    dnpMt: "16px",
    lh: 1.15,
    dmt: 2,
    dhs: "0.3in",
    dpad: "4px 7px 4px 4px",
  },
  roomy: {
    gap: "12px 9px",
    kt: 8,
    kb: 9,
    hs: "0.38in",
    bfs: "18.5px",
    gfs: "16.5px",
    nfs: "14px",
    bpad: "2.5px 7px 2.5px 6px",
    hpad: "5px 7px 5px 5px",
    tfs: "22px",
    dnpMt: "14px",
    lh: 1.15,
    dmt: 2,
    dhs: "0.3in",
    dpad: "4px 7px 4px 4px",
  },
  normal: {
    gap: "9px 8px",
    kt: 6,
    kb: 7,
    hs: "0.34in",
    bfs: "18px",
    gfs: "16px",
    nfs: "14px",
    bpad: "2px 6px 2px 5px",
    hpad: "4px 7px 4px 4px",
    tfs: "21px",
    dnpMt: "12px",
    lh: 1.15,
    dmt: 2,
    dhs: "0.3in",
    dpad: "4px 7px 4px 4px",
  },
  compact: {
    gap: "6px 8px",
    kt: 4,
    kb: 5,
    hs: "0.3in",
    bfs: "17.5px",
    gfs: "15.5px",
    nfs: "13.5px",
    bpad: "1.5px 6px 1.5px 5px",
    hpad: "3px 6px 3px 4px",
    tfs: "20px",
    dnpMt: "10px",
    lh: 1.15,
    dmt: 2,
    dhs: "0.3in",
    dpad: "4px 7px 4px 4px",
  },
  tight: {
    gap: "3px 7px",
    kt: 1.5,
    kb: 2,
    hs: "0.24in",
    bfs: "15.5px",
    gfs: "13.5px",
    nfs: "12px",
    bpad: "0.5px 5px 0.5px 4px",
    hpad: "2px 6px 2px 3px",
    tfs: "17px",
    dnpMt: "8px",
    lh: 1.15,
    dmt: 2,
    dhs: "0.3in",
    dpad: "4px 7px 4px 4px",
  },
  micro: {
    gap: "2px 7px",
    kt: 0.5,
    kb: 0.5,
    hs: "0.2in",
    bfs: "14px",
    gfs: "12.5px",
    nfs: "11.5px",
    bpad: "0 4px 0 3px",
    hpad: "0 6px 0 3px",
    tfs: "15px",
    dnpMt: "5px",
    lh: 1.05,
    dmt: 0,
    dhs: "0.24in",
    dpad: "2px 7px 2px 4px",
  },
};
const STEPS = ["airy", "roomy", "normal", "compact", "tight", "micro"] as const;
// The footnote's bottom, in CSS px from the sheet's top (96 dpi), must clear the footer: the design's thresholds.
const MAX_BOTTOM = 978;
const STRETCH_BELOW = 950;

/** Fits the page: returns [density index, stretch px, settled]. Re-runs when `key` (the data) changes. `last` is the
 * densest step it may take. */
function useFit(
  ref: React.RefObject<HTMLElement>,
  key: unknown,
  playedRows: number,
  last: number,
) {
  const [dens, setDens] = useState(0);
  const [stretch, setStretch] = useState(0);
  const [stretched, setStretched] = useState(false);
  const [settled, setSettled] = useState(false);
  const [overflow, setOverflow] = useState(false);
  useLayoutEffect(() => {
    setDens(0);
    setStretch(0);
    setStretched(false);
    setSettled(false);
    setOverflow(false);
  }, [key]);
  useLayoutEffect(() => {
    const s = ref.current;
    const fn = s?.querySelector(".fn") as HTMLElement | null;
    if (!s || !fn) return;
    const r = s.getBoundingClientRect();
    const sc = r.width / s.offsetWidth || 1;
    const bottom = (fn.getBoundingClientRect().bottom - r.top) / sc;
    if (bottom > MAX_BOTTOM) {
      if (stretch) setStretch(Math.max(0, stretch - 1));
      else if (dens < last) setDens(dens + 1);
      else {
        // Still too tall at the densest step: more played cards than a sheet holds (a dashboard window).
        setOverflow(true);
        setSettled(true);
      }
    } else if (!stretched && bottom < STRETCH_BELOW) {
      setStretch(
        Math.min(8, Math.floor((970 - bottom) / (Math.max(1, playedRows) * 6))),
      );
      setStretched(true);
    } else {
      setSettled(true);
    }
    // Each step changes the layout, so the measure re-runs on the step it caused until the page fits.
  }, [ref, key, dens, stretch, stretched, playedRows, last]);
  return { dens, stretch, settled, overflow };
}

export default function PlayerKpiPage({
  data,
  hrefFor,
  onSettled,
  pageLabel = packetPageLabel("Player KPIs", "players"),
  windowLabel,
  win,
  grow = false,
  goalsLabel,
}: {
  data: KpiResponse;
  /** Each card's player page. Left off on the packet's print route: a link becomes a clickable annotation in the PDF
   * (Trevor 2026-10-11). The screen pages keep it, except while the browser prints them. */
  hrefFor?: (p: PlayerModel) => string;
  /** Whether the page has finished fitting itself, and whether a one-sheet page still overflows at its densest step
   * (the packet reports that rather than printing a clipped sheet). */
  onSettled?: (s: boolean, overflow: boolean) => void;
  pageLabel?: string;
  /** A dashboard window ("Last 10"): it replaces "tonight" in the labels. */
  windowLabel?: string;
  /** A multi-game window's header furniture (title + W-L box), dashboard only. */
  win?: WindowHead | null;
  /** The dashboard: a roster too long for one sheet (more than ~15 played over a window) grows the page instead of
   * clipping it, from the tight step (micro is for a printed sheet). The packet never sets this: it must stay one
   * sheet. */
  grow?: boolean;
  /** A past season on the dashboard, measured against this season's list: "2026-27 goals" (Trevor 2026-10-07). */
  goalsLabel?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const printing = usePrinting();
  const linkFor = printing ? undefined : hrefFor;
  const game = data.meta.game!;
  const live = data.meta.live;
  const liveLabel = live ? periodLabel(live.throughPeriod) : null;
  // Before the end of regulation a player who hasn't appeared may still come in: "Not in yet", then "DNP".
  const notInYet = !!live && live.throughPeriod < 4;
  const g = liveMeta(
    gameMeta(game, data.meta.porGamesToDate),
    live?.throughPeriod,
  );
  const { players } = buildPlayers(data.players || []);
  const played = players.filter((p) => p.played);
  const sat = players.filter((p) => !p.played);
  const fit = useFit(
    ref,
    data,
    Math.ceil(played.length / 3),
    grow ? STEPS.indexOf("tight") : STEPS.length - 1,
  );
  const tall = grow && fit.overflow;
  // A grown page has room: it goes back to the normal step rather than the tight one that still didn't fit.
  const dens = tall ? STEPS.indexOf("normal") : fit.dens;
  const stretch = tall ? 0 : fit.stretch;
  const settled = fit.settled;
  const clipped = fit.overflow && !grow;
  useEffect(() => {
    onSettled?.(settled, clipped);
  }, [settled, clipped, onSettled]);
  const D = DENSITY[STEPS[dens]];
  const kpad = `${D.kt + stretch}px 7px ${D.kb + stretch}px`;
  const gap = `${parseFloat(D.gap) + stretch}px ${D.gap.split(" ")[1]}`;
  let metN = 0;
  let metD = 0;
  for (const p of played) {
    metN += p.pm;
    metD += p.pd;
  }
  return (
    <section
      className={tall ? "page tall" : "page"}
      ref={ref}
      data-screen-label="Player KPIs"
      data-density={STEPS[dens]}
    >
      <Masthead
        g={g}
        who={pageLabel}
        kicker="Portland Trail Blazers · Player Development"
        title="Player KPIs"
        report={live ? "Live KPIs" : undefined}
        win={win}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          borderTop: "2px solid #000",
          borderBottom: "1px solid #cfccc8",
          padding: "6px 0 7px",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontFamily: DISP,
            fontWeight: 800,
            fontSize: 36,
            lineHeight: 0.9,
            whiteSpace: "nowrap",
          }}
        >
          {metN} of {metD}
        </div>
        <div
          style={{
            fontFamily: DISP,
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            lineHeight: 1.15,
          }}
        >
          {goalsLabel ? `${goalsLabel} met` : "Goals met"}
          <br />
          {windowLabel || liveLabel || "tonight"}
        </div>
        <Legend />
      </div>

      <div className="grp" style={{ margin: "0 0 6px" }}>
        <span className="k">
          {windowLabel ? `Played · ${windowLabel}` : "Played tonight"}
        </span>
        <span className="bar" />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,minmax(0,1fr))",
          gap,
        }}
      >
        {played.map((p) => (
          <PlayedCard
            key={p.playerId}
            p={p}
            D={D}
            kpad={kpad}
            href={linkFor ? linkFor(p) : undefined}
          />
        ))}
      </div>

      {/* Everyone played (a preseason game can play the whole camp roster): no empty heading. */}
      {sat.length > 0 && (
        <>
          <div className="grp" style={{ margin: `${D.dnpMt} 0 6px` }}>
            <span className="k">
              {windowLabel
                ? `No games · ${windowLabel}`
                : notInYet
                  ? "Not in yet"
                  : "Did not play"}
            </span>
            <span className="bar" />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6,minmax(0,1fr))",
              gap: 7,
            }}
          >
            {sat.map((p) => (
              <CardLink
                key={p.playerId}
                href={linkFor ? linkFor(p) : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  border: "1px solid #cfccc8",
                  background: "#fff",
                  minWidth: 0,
                  color: "inherit",
                  padding: D.dpad,
                }}
              >
                <Headshot src={p.img} size={D.dhs} />
                <span style={{ minWidth: 0, lineHeight: 1.15 }}>
                  <span
                    style={{
                      display: "block",
                      fontFamily: DISP,
                      fontWeight: 700,
                      fontSize: 13,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.last}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 10,
                      color: "#3a3a3a",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    {notInYet && p.status === "dnp"
                      ? "Not in yet"
                      : satStatus(p)}
                  </span>
                </span>
              </CardLink>
            ))}
          </div>
        </>
      )}
      <div className="fn" />
      <Footer
        left={win ? "Portland Trail Blazers · KPI Dashboard" : undefined}
        right={`Player KPIs · ${win ? win.line : g.line}`}
      />
    </section>
  );
}

export function Headshot({ src, size }: { src: string; size: string }) {
  return (
    <span
      style={{
        display: "block",
        width: size,
        height: size,
        flexShrink: 0,
        background: "#efedeb",
        border: "1px solid #cfccc8",
        overflow: "hidden",
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center top",
          display: "block",
        }}
      />
    </span>
  );
}

function PlayedCard({
  p,
  D,
  kpad,
  href,
}: {
  p: PlayerModel;
  D: (typeof DENSITY)[keyof typeof DENSITY];
  kpad: string;
  href?: string;
}) {
  const head: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: D.hpad,
    background: "#f5f5f5",
    borderBottom: "2px solid #4c4c4c",
  };
  return (
    <CardLink
      href={href}
      style={{
        display: "block",
        border: "1px solid #cfccc8",
        background: "#fff",
        minWidth: 0,
        color: "inherit",
      }}
    >
      <div style={head}>
        <Headshot src={p.img} size={D.hs} />
        <div style={{ minWidth: 0, lineHeight: D.lh, flex: 1 }}>
          <div
            style={{
              fontFamily: DISP,
              fontWeight: 700,
              fontSize: D.nfs,
              textTransform: "uppercase",
              letterSpacing: "0.01em",
            }}
          >
            {p.name}
          </div>
          <div style={{ fontSize: 10, color: "#6b6864" }}>
            {p.poss != null ? `${p.poss} poss` : ""}
          </div>
        </div>
        <div
          style={{ textAlign: "right", lineHeight: 1, whiteSpace: "nowrap" }}
        >
          <div style={{ fontFamily: DISP, fontWeight: 800, fontSize: D.tfs }}>
            {p.pd ? `${p.pm} of ${p.pd}` : "—"}
          </div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "#6b6864",
              fontWeight: 700,
              marginTop: 1,
            }}
          >
            met
          </div>
        </div>
      </div>
      {p.kpis.map((k: KpiModel) => (
        <div
          key={k.key}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) 62px 50px",
            columnGap: 7,
            alignItems: "center",
            padding: kpad,
            borderTop: "1px solid #e6e4e1",
          }}
        >
          <span style={{ minWidth: 0, lineHeight: D.lh }}>
            <span
              style={{
                display: "block",
                fontWeight: 700,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "-0.005em",
                whiteSpace: "nowrap",
              }}
            >
              {k.label}
            </span>
            <span
              style={{
                display: "block",
                fontSize: 10,
                color: "#6b6864",
                marginTop: D.dmt,
              }}
            >
              {k.game.text}
              {k.unit ? ` · ${k.unit}` : ""}
            </span>
          </span>
          <ValueBlock
            m={k.game.mark}
            value={k.game.noAtt || k.game.unavail ? "—" : k.game.v}
            padding={D.bpad}
            width="62px"
            fontSize={D.bfs}
          />
          <span style={{ lineHeight: 1.1, whiteSpace: "nowrap" }}>
            <span
              style={{
                display: "block",
                fontSize: 10,
                color: "#6b6864",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 700,
              }}
            >
              Goal
            </span>
            <b
              style={{
                display: "block",
                fontFamily: DISP,
                fontWeight: 700,
                fontSize: D.gfs,
                marginTop: 1,
              }}
            >
              {k.goal}
            </b>
          </span>
        </div>
      ))}
    </CardLink>
  );
}

/** A card that opens the player's page on screen; with no `href` (the packet's print route, or the browser printing
 * a screen page) the same box as a plain element, so the PDF carries no link annotations. */
export function CardLink({
  href,
  style,
  children,
}: {
  href?: string;
  style: CSSProperties;
  children: ReactNode;
}) {
  return href ? (
    <Link to={href} title="Open player page" style={style}>
      {children}
    </Link>
  ) : (
    <div style={style}>{children}</div>
  );
}
